import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ManualBody } from "@arc/contracts";
import { checkManual, type ManualCheckResult } from "./manual-check.js";
import { CheckManualDialog } from "./CheckManualDialog.js";

/** The two publish-direction transitions the check guards. Backward moves — return-to-draft, unmark — are not gated:
 *  the legacy gates neither, and there is nothing to validate about un-publishing. */
export type GatedTransition = "submit" | "confirm";

interface Pending {
  action: GatedTransition;
  results: ManualCheckResult[];
  proceed: () => void;
  onSettled?: () => void;
}

/**
 * The Check Manual gate, as ONE implementation both doors share (ADR-018 addendum §9).
 *
 * The gate was built for the editor, which is the only door in realms that can reach one. Classic-nav realms
 * (novonordisk, lumpi, abzbraunau) have no editor at all, so they publish from the manuals LIST — and for as long as
 * the gate lived only in `EditorStatusControl`, three of five tenants published with no check, while the legacy
 * checked them all. Rather than write the wiring a second time and let the two drift, the state and the dialog live
 * here and each door supplies the freshest body it can reach: the editor reads its post-flush cache, the list fetches.
 *
 * **Advisory, and that shapes the no-body case.** A caller that cannot produce a body calls `proceed` straight
 * through rather than blocking — the server keeps its own authz and state CAS regardless, so refusing to publish
 * because an advisory summary could not be computed would fail in the wrong direction. It stays a warning, not a lock.
 */
export function useManualCheckGate(): {
  /** Run the check over `body` and hold `proceed` until the author answers. A missing body fires `proceed` at once.
   *  `onSettled` runs exactly once when the gate concludes — continued, cancelled, or passed straight through — so a
   *  caller can hold a "a publish is being decided" flag without tracking the three exits itself. */
  gate: (body: ManualBody | null | undefined, action: GatedTransition, proceed: () => void, onSettled?: () => void) => void;
  /** Render this — it is the dialog, or null. */
  dialog: ReactNode;
} {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);

  const gate = useCallback(
    (body: ManualBody | null | undefined, action: GatedTransition, proceed: () => void, onSettled?: () => void) => {
      // The dialog opens whether or not anything failed: it is a summary, and one that only appears on failure trains
      // people to dismiss it (the legacy shows it unconditionally too).
      if (!body) {
        proceed();
        onSettled?.();
        return;
      }
      setPending({ action, results: checkManual(body, t), proceed, onSettled });
    },
    [t],
  );

  const dialog = pending ? (
    <CheckManualDialog
      results={pending.results}
      action={pending.action}
      onCancel={() => {
        setPending(null);
        pending.onSettled?.();
      }}
      onContinue={() => {
        // Read the callbacks out BEFORE clearing — `setPending(null)` is what unmounts the dialog, and calling
        // through `pending` afterwards would read the captured object anyway, but doing it in this order says so.
        const { proceed, onSettled } = pending;
        setPending(null);
        proceed();
        onSettled?.();
      }}
    />
  ) : null;

  return { gate, dialog };
}
